import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IAddCheckingTeacherData,
  IGroup,
  IRefreshCheckingTeacherHomeworks,
} from '@interfaces/IGroup';
import { IHappyPointMutation } from '@interfaces/IHappyPoint';
import { IUser } from '@interfaces/IUser';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GroupsChangesService {
  constructor(private _http: HttpClient) {}

  create(data: IGroup): Observable<IGroup> {
    const url = `${environment.baseUrl}api/v1/groups/`;
    return this._http.post<IGroup>(url, data);
  }

  update(id: number, data: IGroup): Observable<IGroup> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/`;
    return this._http.patch<IGroup>(url, data);
  }

  delete(id: number): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/`;
    return this._http.delete<{ detail: string }>(url);
  }

  deactivate(id: number): Observable<IGroup> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/`;
    return this._http.patch<IGroup>(url, { is_active: false });
  }

  activate(id: number): Observable<IGroup> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/`;
    return this._http.patch<IGroup>(url, { is_active: true });
  }

  transferToESDP(
    id: number,
    data: {
      lesson_days: string;
      period_start_date: string;
      is_esdp: boolean;
    }
  ): Observable<IGroup> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/`;
    return this._http.patch<IGroup>(url, { ...data });
  }

  studentsTransfer(
    id: number,
    data: { users: number[]; group: number }
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/students/transfer/`;
    return this._http.post<{ detail: string }>(url, data);
  }

  addAdminsToTGGroup(data: { group?: number; team?: number; users: number[] }) {
    const url = `${environment.baseUrl}api/v1/add-tg/`;
    return this._http.post(url, data);
  }

  addStudents(
    id: number,
    students: number[]
  ): Observable<{ detail: string; added: IUser[] }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/students/`;
    return this._http.post<{ detail: string; added: IUser[] }>(url, {
      users: students,
    });
  }

  addTeachers(id: number, teachers: number[]): Observable<{ added: IUser[] }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/teachers/`;
    return this._http.post<{ added: IUser[] }>(url, { users: teachers });
  }

  addCheckingTeacher(data: IAddCheckingTeacherData) {
    const url = `${environment.baseUrl}api/v1/reviewers-groups/`;
    return this._http.post(url, data);
  }

  refreshCheckingTeacherHomeworks(
    group_id: number,
    data: IRefreshCheckingTeacherHomeworks
  ) {
    const url = `${environment.baseUrl}api/v1/groups/${group_id}/teacher-works-reviews-distribute/`;
    return this._http.post(url, data);
  }

  deleteCheckingTeacher(teacher_id: number) {
    const url = `${environment.baseUrl}api/v1/reviewers-groups/${teacher_id}/`;
    return this._http.delete(url);
  }

  dropoutStudent(
    id: number,
    students: number[]
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/students/`;
    return this._http.delete<{ detail: string }>(url, {
      body: { users: students },
    });
  }

  activateStudent(
    id: number,
    students: number[]
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/students/`;
    return this._http.patch<{ detail: string }>(url, { users: students });
  }

  setStudentOnline(
    groupId: number,
    studentId: number
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${groupId}/students/${studentId}/change-format/`;
    return this._http.patch<{ detail: string }>(url, null);
  }

  deleteTeachers(
    id: number,
    teachers: number[]
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/teachers/`;
    return this._http.delete<{ detail: string }>(url, {
      body: { users: teachers },
    });
  }

  deleteTeacherFromSelectedGroups(
    groups: number[],
    teacher: number
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/teacher-delete/`;
    const data = { groups, teacher };
    return this._http.post<{ detail: string }>(url, data);
  }

  editStudentAccess(
    id: number,
    data: { start_date: string; end_date: string; students: number[] }
  ): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/students/edit_access/`;
    return this._http.post<{ detail: string }>(url, data);
  }

  returnTeacher(id: number, users: number[]): Observable<{ detail: string }> {
    const url = `${environment.baseUrl}api/v1/groups/${id}/teachers/`;
    return this._http.patch<{ detail: string }>(url, { users });
  }

  studentHappyPointAllowed(groupId: number, data: IHappyPointMutation) {
    const url = `${environment.baseUrl}api/v1/groups/${groupId}/students/happy-point/`;
    return this._http.post(url, data);
  }

  deleteTeacherFromGroup(groupId: number, users: number) {
    const url = `${environment.baseUrl}api/v1/groups/${groupId}/teachers/teacher-hard-delete/`;
    return this._http.put(url, { users });
  }
}
